import { ORGANIZATION_MONTHLY_USER } from "@/gql/organization";
import { IOrganization } from "@/types/organization";
import { DATE_FORMAT } from "@/utils/common";
import { handleApolloError } from "@/utils/handle-form";
import { useQuery } from "@apollo/client";
import {
  IOrganizationMonthlyUserQuery,
  IOrganizationMonthlyUserQueryVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { PaymentPlanType } from "@shared/common/types/organization";
import organizationHelper, {
  PAYMENT_ALERT_STATUS_TYPE,
} from "@shared/common/utils/organization-helper";
import { toFirstUpperCaseOtherLowerCase } from "@shared/common/utils/string-util";
import dayjs from "dayjs";
import Link from "next/link";

const v2UrlPrefix = process.env.NEXT_PUBLIC_V2_URL_PREFIX;

interface IPaymentAlertProps {
  currentOrganization: IOrganization;
}
const PaymentAlert = ({ currentOrganization }: IPaymentAlertProps) => {
  const { planEndAt, nextBilledAt, billingFailCount, status } =
    currentOrganization;
  const alertStatusCode = organizationHelper.getPaymentAlertStatus(
    planEndAt,
    nextBilledAt,
    billingFailCount,
    status
  );

  const { data } = useQuery<
    IOrganizationMonthlyUserQuery,
    IOrganizationMonthlyUserQueryVariables
  >(ORGANIZATION_MONTHLY_USER, {
    variables: {
      organizationId: currentOrganization.id,
    },
    skip: alertStatusCode !== PAYMENT_ALERT_STATUS_TYPE.NOTICE_EXPECT_PLAN,
    onError: handleApolloError,
  });

  const organizationMonthlyUser = data?.organizationMonthlyUser;

  const nextPlan = organizationHelper.getNextRenewalPlan(
    (currentOrganization.scheduledChangePlan ??
      currentOrganization.plan) as PaymentPlanType,
    (organizationMonthlyUser?.count || 0) / 1.1
  );

  const RenderAlert = () => {
    switch (alertStatusCode) {
      case PAYMENT_ALERT_STATUS_TYPE.NOTICE_EXPECT_PLAN:
        return (
          <Link href={`${v2UrlPrefix}/${currentOrganization.slug}/payment`}>
            <div className="mb-6 flex items-center gap-5 bg-[#000123BF] text-white justify-center shadow-[0_2px_2px_0_rgba(0,0,0,0.05)] px-9 py-2.5">
              {dayjs(currentOrganization.nextBilledAt).format(DATE_FORMAT)}{" "}
              &apos;{toFirstUpperCaseOtherLowerCase(nextPlan)}&lsquo;으로 결제
              예정입니다. 결제정보를 확인해주세요.{" "}
              <span className="px-4 py-1 text-sm border rounded">클릭</span>
            </div>
          </Link>
        );
      case PAYMENT_ALERT_STATUS_TYPE.PAYMENT_NOT_PROCEED:
        return (
          <Link href={`${v2UrlPrefix}/${currentOrganization.slug}/payment`}>
            <div className="mb-6 flex items-center gap-5 bg-secondary-300 text-neutral-800 justify-center shadow-[0_2px_2px_0_rgba(0,0,0,0.05)] px-9 py-2.5">
              {dayjs(currentOrganization.nextBilledAt)
                .subtract(1, "day")
                .format(DATE_FORMAT)}{" "}
              결제가 정상적으로 진행되지 않았습니다. 카드 정보를
              다시입력해주세요.
              <span className="px-4 py-1 text-sm border rounded border-neutral-800">
                클릭
              </span>
            </div>
          </Link>
        );
      case PAYMENT_ALERT_STATUS_TYPE.FAIL_PAYMENT:
        return (
          <Link href={`${v2UrlPrefix}/${currentOrganization.slug}/payment`}>
            <div className="mb-6 flex items-center gap-5 bg-[#EB4256B2] text-white justify-center shadow-[0_2px_2px_0_rgba(0,0,0,0.05)] px-9 py-2.5">
              결제 실패로 서비스 사용이 제한 되었습니다. 카드 정보를
              다시입력해주세요.
              <span className="px-4 py-1 text-sm border rounded">클릭</span>
            </div>
          </Link>
        );
      default:
        return null;
    }
  };

  return <RenderAlert />;
};

export default PaymentAlert;
